import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { fetchAttractions as fetchAttractionsApi, getAttraction } from "@/lib/attractionsApi";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { NumericInput } from "@/components/ui/numeric-input";
import { DateInput } from "@/components/ui/date-input";
import { computeItemBaseAmount } from "@/lib/financialCalculations";
import { PriceWithAdjustment } from "@/components/proposals/PriceWithAdjustment";

interface AttractionsStepProps {
  data: any;
  onChange: (data: any) => void;
  readOnly?: boolean;
}

interface AttractionTicketEntry {
  local_id: string;
  id?: number;
  ticket_id: number | null;
  ticket_quantity: number;
  free_of_charge: boolean;
  foc_quantity: number;
  notes: string;
  is_selected_by_client?: boolean;
  attraction_tickets?: any;
}

interface AttractionEntry {
  local_id: string;
  attraction_id: number | null;
  attraction_date: string;
  time_from: string;
  time_to: string;
  attractions?: any;
  tickets: AttractionTicketEntry[];
}

const AttractionsStep = ({ data, onChange, readOnly }: AttractionsStepProps) => {
  const isReadOnly = !!readOnly;
  const [attractions, setAttractions] = useState<any[]>([]);
  const [ticketsByAttraction, setTicketsByAttraction] = useState<Record<number, any[]>>({});
  const [attractionEntries, setAttractionEntries] = useState<AttractionEntry[]>([]);
  const localIdRef = useRef(0);
  const lastInternalSyncSignatureRef = useRef<string>("");

  const nextLocalId = (prefix: string) => {
    localIdRef.current += 1;
    return `${prefix}_${Date.now()}_${localIdRef.current}`;
  };

  const createTicketEntry = (source?: any): AttractionTicketEntry => ({
    local_id: nextLocalId("ticket"),
    id: typeof source?.id === "number" ? source.id : undefined,
    ticket_id: typeof source?.ticket_id === "number" ? source.ticket_id : null,
    ticket_quantity: Number(source?.ticket_quantity ?? 1),
    free_of_charge: !!source?.free_of_charge,
    foc_quantity: Number(source?.foc_quantity ?? 0),
    notes: (source?.notes || "").toString(),
    is_selected_by_client: source?.is_selected_by_client,
    attraction_tickets: source?.attraction_tickets,
  });

  const createAttractionEntry = (source?: any): AttractionEntry => ({
    local_id: nextLocalId("entry"),
    attraction_id: typeof source?.attraction_id === "number" ? source.attraction_id : null,
    attraction_date: (source?.attraction_date || "").toString(),
    time_from: (source?.time_from || "").toString(),
    time_to: (source?.time_to || "").toString(),
    attractions: source?.attractions,
    tickets: source ? [createTicketEntry(source)] : [createTicketEntry()],
  });

  const groupIncomingAttractions = (items: any[]): AttractionEntry[] => {
    const grouped = new Map<string, AttractionEntry>();
    const order: string[] = [];

    (items || []).forEach((item: any, index: number) => {
      const attractionId = typeof item?.attraction_id === "number" ? item.attraction_id : null;
      const key = attractionId !== null
        ? `${attractionId}__${item?.attraction_date || ""}__${item?.time_from || ""}__${item?.time_to || ""}`
        : `new_${index}`;

      if (!grouped.has(key)) {
        const entry = createAttractionEntry(item);
        entry.tickets = [];
        grouped.set(key, entry);
        order.push(key);
      }

      grouped.get(key)!.tickets.push(createTicketEntry(item));
    });

    return order.map((key) => {
      const entry = grouped.get(key)!;
      if (entry.tickets.length === 0) {
        entry.tickets = [createTicketEntry()];
      }
      return entry;
    });
  };

  const flattenEntries = (entries: AttractionEntry[]) =>
    entries.flatMap((entry) =>
      entry.tickets.map((ticket) => ({
        id: ticket.id,
        attraction_id: entry.attraction_id,
        ticket_id: ticket.ticket_id,
        attraction_date: entry.attraction_date,
        time_from: entry.time_from,
        time_to: entry.time_to,
        ticket_quantity: Number(ticket.ticket_quantity ?? 1),
        free_of_charge: !!ticket.free_of_charge,
        foc_quantity: Number(ticket.foc_quantity ?? 0),
        notes: ticket.notes || "",
        is_selected_by_client: ticket.is_selected_by_client,
        attractions: entry.attractions,
        attraction_tickets: ticket.attraction_tickets,
      }))
    );

  const buildSyncSignature = (items: any[] | undefined | null) =>
    JSON.stringify(
      (items || []).map((item: any) => ({
        id: typeof item?.id === "number" ? item.id : null,
        attraction_id: typeof item?.attraction_id === "number" ? item.attraction_id : null,
        ticket_id: typeof item?.ticket_id === "number" ? item.ticket_id : null,
        attraction_date: (item?.attraction_date || "").toString(),
        time_from: (item?.time_from || "").toString(),
        time_to: (item?.time_to || "").toString(),
        ticket_quantity: Number(item?.ticket_quantity ?? 1),
        free_of_charge: !!item?.free_of_charge,
        foc_quantity: Number(item?.foc_quantity ?? 0),
        notes: (item?.notes || "").toString(),
      }))
    );

  const emitEntriesChange = (entries: AttractionEntry[]) => {
    const flattened = flattenEntries(entries);
    lastInternalSyncSignatureRef.current = buildSyncSignature(flattened);
    onChange({ ...data, attractions: flattened });
  };

  const commitEntries = (entries: AttractionEntry[]) => {
    setAttractionEntries(entries);
    emitEntriesChange(entries);
  };

  const updateEntries = (updater: (previous: AttractionEntry[]) => AttractionEntry[]) => {
    setAttractionEntries((previous) => {
      const next = updater(previous);
      emitEntriesChange(next);
      return next;
    });
  };

  useEffect(() => {
    const loadAttractions = async () => {
      const payload = await fetchAttractionsApi({ page: 1, size: 1000, sortField: "title", sortOrder: "asc" });
      setAttractions(payload.items || []);
    };
    loadAttractions();
  }, []);

  const fetchTickets = async (attractionId: number) => {
    if (ticketsByAttraction[attractionId]) return;
    try {
      const attraction = await getAttraction(attractionId);
      setTicketsByAttraction((previous) => ({ ...previous, [attractionId]: attraction?.tickets || [] }));
    } catch {
      setTicketsByAttraction((previous) => ({ ...previous, [attractionId]: [] }));
    }
  };

  useEffect(() => {
    const incomingAttractions = Array.isArray(data.attractions) ? data.attractions : [];
    const incomingSignature = buildSyncSignature(incomingAttractions);

    // Ignore state echo from parent after local edits to preserve local_id and input focus.
    if (incomingSignature === lastInternalSyncSignatureRef.current) {
      return;
    }

    if (incomingAttractions.length === 0) {
      setAttractionEntries([]);
      return;
    }

    const groupedEntries = groupIncomingAttractions(incomingAttractions);
    setAttractionEntries(groupedEntries);
    groupedEntries.forEach((entry) => {
      if (entry.attraction_id) {
        void fetchTickets(entry.attraction_id);
      }
    });
  }, [data.attractions]);

  const addAttractionEntry = () => {
    if (isReadOnly) return;
    commitEntries([createAttractionEntry(), ...attractionEntries]);
  };

  const removeAttractionEntry = (entryIndex: number) => {
    if (isReadOnly) return;
    const nextEntries = attractionEntries.filter((_, index) => index !== entryIndex);
    commitEntries(nextEntries);
  };

  const updateAttractionField = async (entryIndex: number, field: keyof AttractionEntry, value: any) => {
    if (isReadOnly) return;
    const previousEntries = attractionEntries;
    const currentEntry = previousEntries[entryIndex];
    if (!currentEntry) return;

    if (field === "attraction_id") {
      const attractionId = Number(value);
      const normalizedAttractionId = Number.isFinite(attractionId) ? attractionId : null;

      updateEntries((entries) =>
        entries.map((entry, index) => {
          if (index !== entryIndex) return entry;
          return {
            ...entry,
            attraction_id: normalizedAttractionId,
            attractions: undefined,
            tickets: entry.tickets.map((ticket) => ({
              ...ticket,
              ticket_id: null,
              attraction_tickets: undefined,
            })),
          };
        })
      );

      if (!normalizedAttractionId) return;

      await fetchTickets(normalizedAttractionId);
      try {
        const selectedAttraction = await getAttraction(normalizedAttractionId);
        updateEntries((entries) =>
          entries.map((entry, index) => {
            if (index !== entryIndex) return entry;
            if (entry.attraction_id !== normalizedAttractionId) return entry;
            return {
              ...entry,
              attractions: {
                id: selectedAttraction.id,
                attraction_code: selectedAttraction.attraction_code,
                title: selectedAttraction.title,
                description: selectedAttraction.description,
                image_url: selectedAttraction.image_url,
                opening_hours: selectedAttraction.opening_hours,
              },
            };
          })
        );
      } catch {
        // Keep selected attraction ID even if snapshot fetch fails.
      }

      return;
    }

    updateEntries((entries) =>
      entries.map((entry, index) => {
        if (index !== entryIndex) return entry;
        return { ...entry, [field]: value };
      })
    );
  };

  const addTicketType = (entryIndex: number) => {
    if (isReadOnly) return;
    updateEntries((entries) =>
      entries.map((entry, index) => {
        if (index !== entryIndex) return entry;
        return {
          ...entry,
          tickets: [
            ...entry.tickets,
            createTicketEntry({ ticket_quantity: 1 }),
          ],
        };
      })
    );
  };

  const removeTicketType = (entryIndex: number, ticketIndex: number) => {
    if (isReadOnly) return;
    updateEntries((entries) =>
      entries.flatMap((entry, index) => {
        if (index !== entryIndex) return [entry];
        if (entry.tickets.length <= 1) return [];
        return [
          {
            ...entry,
            tickets: entry.tickets.filter((_, idx) => idx !== ticketIndex),
          },
        ];
      })
    );
  };

  const updateTicketField = (entryIndex: number, ticketIndex: number, field: keyof AttractionTicketEntry, value: any) => {
    if (isReadOnly) return;
    updateEntries((entries) =>
      entries.map((entry, index) => {
        if (index !== entryIndex) return entry;
        const updatedTickets = entry.tickets.map((ticket, idx) => {
          if (idx !== ticketIndex) return ticket;
          const updatedTicket: AttractionTicketEntry = { ...ticket, [field]: value };

          if (field === "ticket_id") {
            const selectedTicketId = Number(value);
            const selectedTicket = (ticketsByAttraction[entry.attraction_id || 0] || [])
              .find((ticketItem) => ticketItem.id === selectedTicketId);
            updatedTicket.ticket_id = Number.isFinite(selectedTicketId) ? selectedTicketId : null;
            updatedTicket.attraction_tickets = selectedTicket
              ? {
                  id: selectedTicket.id,
                  title: selectedTicket.title,
                  description: selectedTicket.description,
                  pricing_type: selectedTicket.pricing_type,
                  price_op: selectedTicket.price_op,
                  price_ag: selectedTicket.price_ag,
                  price_cf: selectedTicket.price_cf,
                }
              : undefined;
          }

          return updatedTicket;
        });

        return { ...entry, tickets: updatedTickets };
      })
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Attractions</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Add attractions and ticket types for the trip
          </p>
        </div>
        <Button type="button" size="sm" onClick={addAttractionEntry} disabled={isReadOnly}>
          <Plus className="mr-2 h-4 w-4" />
          Add Attraction
        </Button>
      </div>

      {attractionEntries.length === 0 ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${isReadOnly ? "" : "cursor-pointer transition-colors hover:bg-muted/30"}`}
          onClick={isReadOnly ? undefined : addAttractionEntry}
          onKeyDown={isReadOnly ? undefined : (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              addAttractionEntry();
            }
          }}
          role={isReadOnly ? undefined : "button"}
          tabIndex={isReadOnly ? -1 : 0}
          aria-label={isReadOnly ? undefined : "Add attraction"}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="rounded-full bg-muted p-3">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">No attractions added yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Click "Add Attraction" to get started
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {attractionEntries.map((entry, entryIndex) => (
            <div key={entry.local_id} className="grid gap-5 p-6 border rounded-lg bg-card relative shadow-sm hover:shadow-md transition-shadow">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute top-3 right-3 h-8 w-8 p-0"
                onClick={() => removeAttractionEntry(entryIndex)}
                disabled={isReadOnly}
              >
                <X className="h-4 w-4" />
              </Button>

              <div className="grid grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Attraction</Label>
                  <SearchableSelect
                    items={attractions.map((attraction) => ({
                      id: attraction.id,
                      code: attraction.attraction_code,
                      title: attraction.title,
                      description: attraction.supplier_name || undefined,
                    }))}
                    value={entry.attraction_id || undefined}
                    onValueChange={(value) => updateAttractionField(entryIndex, "attraction_id", Number(value))}
                    placeholder="Select attraction"
                    disabled={isReadOnly}
                    fallbackDisplayValue={entry.attractions?.title || undefined}
                  />
                </div>
              </div>

              <div className="border-t pt-5">
                <div className="grid grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date</Label>
                    <DateInput
                      value={entry.attraction_date || ""}
                      onChange={(value) => updateAttractionField(entryIndex, "attraction_date", value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">From Time</Label>
                    <Input
                      type="time"
                      value={entry.time_from || ""}
                      onChange={(event) => updateAttractionField(entryIndex, "time_from", event.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">To Time</Label>
                    <Input
                      type="time"
                      value={entry.time_to || ""}
                      onChange={(event) => updateAttractionField(entryIndex, "time_to", event.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Ticket Types</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => addTicketType(entryIndex)}
                    disabled={isReadOnly || !entry.attraction_id}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Ticket Type
                  </Button>
                </div>

                {entry.tickets.map((ticket, ticketIndex) => (
                  <div key={ticket.local_id} className="rounded-lg border p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Ticket #{ticketIndex + 1}</p>
                      {entry.tickets.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => removeTicketType(entryIndex, ticketIndex)}
                          disabled={isReadOnly}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Ticket Type</Label>
                      <SearchableSelect
                        items={(ticketsByAttraction[entry.attraction_id || 0] || []).map((ticketItem) => ({
                          id: ticketItem.id,
                          title: ticketItem.title,
                          description: ticketItem.description,
                        }))}
                        value={ticket.ticket_id || undefined}
                        onValueChange={(value) => updateTicketField(entryIndex, ticketIndex, "ticket_id", Number(value))}
                        placeholder="Select ticket"
                        disabled={isReadOnly || !entry.attraction_id}
                        fallbackDisplayValue={ticket.attraction_tickets?.title || undefined}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Ticket Quantity</Label>
                        <NumericInput
                          value={ticket.ticket_quantity ?? 1}
                          onChange={(value) => {
                            const parsed = parseInt(String(value), 10);
                            if (!Number.isFinite(parsed)) return;
                            updateTicketField(entryIndex, ticketIndex, "ticket_quantity", Math.max(0, parsed));
                          }}
                          allowDecimals={false}
                          disabled={isReadOnly}
                        />
                      </div>
                      <div className="flex items-center space-x-2 pt-8">
                        <Checkbox
                          checked={!!ticket.free_of_charge}
                          onCheckedChange={(checked) =>
                            updateTicketField(entryIndex, ticketIndex, "free_of_charge", Boolean(checked))
                          }
                          disabled={isReadOnly}
                        />
                        <Label className="text-sm font-normal cursor-pointer">Free of Charge (FOC)</Label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Notes</Label>
                      <Textarea
                        value={ticket.notes || ""}
                        onChange={(event) => updateTicketField(entryIndex, ticketIndex, "notes", event.target.value)}
                        placeholder="Add any additional notes for this ticket type..."
                        className="min-h-[90px]"
                        disabled={isReadOnly}
                      />
                    </div>

                    <div className="flex items-end justify-end border-t pt-3">
                      <div className="text-right">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ticket total</p>
                        <PriceWithAdjustment
                          baseAmount={
                            ticket.free_of_charge
                              ? 0
                              : computeItemBaseAmount(
                                  ticket as any,
                                  ticket.attraction_tickets,
                                  data,
                                )
                          }
                          proposalData={data}
                          emptyLabel={
                            ticket.free_of_charge
                              ? "Free"
                              : !ticket.ticket_id
                                ? "—"
                                : undefined
                          }
                          size="md"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttractionsStep;
